import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ShoppingBag,
  Download,
  User,
  Settings,
  Loader2,
  Package,
  Calendar,
  Mail,
  Phone,
  Trash2,
  Clock,
  AlertTriangle,
  History,
} from "lucide-react";
import { toast } from "sonner";
import RecommendationsCarousel from "@/components/RecommendationsCarousel";
import { ProgressWidget } from "@/components/ProgressWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState({
    name: "",
    phone: ""
  });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/login", { state: { from: { pathname: "/dashboard" } } });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        phone: user.phone || ""
      });
      fetchPurchases();
    }
  }, [user]);

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/user/purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(response.data);
    } catch (error) {
      console.error("Error fetching purchases:", error);
    } finally {
      setLoading(false);
    }
  };

  const hideOrder = async (transactionRef) => {
    try {
      await axios.post(`${API}/user/orders/hide`, { transaction_ref: transactionRef }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(prev => prev.filter(p => p.transaction_ref !== transactionRef));
      toast.success("Order removed from history");
    } catch (error) {
      toast.error("Failed to remove order");
    }
  };

  const clearAllHistory = async () => {
    if (!window.confirm("Are you sure you want to clear your entire order history? Your download links will still work.")) return;
    try {
      await axios.delete(`${API}/user/orders/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases([]);
      toast.success("Order history cleared");
    } catch (error) {
      toast.error("Failed to clear history");
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/user/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile updated successfully");
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold">My Dashboard</h1>
          <p className="text-muted-foreground mt-1">Welcome back, {user.name || user.email}</p>
        </div>

        {/* Recommendations Carousel */}
        <div className="-mx-4 sm:-mx-6 lg:-mx-8 mb-6">
          <RecommendationsCarousel />
        </div>

        {/* Quiz Progress Widget */}
        <div className="mb-6">
          <ProgressWidget />
        </div>

        <Tabs defaultValue="purchases" className="space-y-6">
          <TabsList className="bg-secondary/50 p-1">
            <TabsTrigger value="purchases" className="gap-2" data-testid="tab-purchases">
              <ShoppingBag className="w-4 h-4" />
              My Purchases
            </TabsTrigger>
            <TabsTrigger value="order-history" className="gap-2" data-testid="tab-order-history">
              <History className="w-4 h-4" />
              Order History
            </TabsTrigger>
            <TabsTrigger value="account" className="gap-2" data-testid="tab-account">
              <User className="w-4 h-4" />
              Account
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Purchases Tab */}
          <TabsContent value="purchases">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : purchases.length > 0 ? (
              <div className="space-y-4">
                {purchases.map((purchase) => (
                  <Card key={purchase.id}>
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                            <Package className="w-4 h-4" />
                            <span>Order {purchase.transaction_ref}</span>
                            <span>•</span>
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(purchase.created_at)}</span>
                          </div>
                          <div className="space-y-1">
                            {purchase.items.map((item, index) => (
                              <p key={index} className="font-medium">{item.title}</p>
                            ))}
                          </div>
                          <p className="text-primary font-bold mt-2">
                            {formatPrice(purchase.total_amount)}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {purchase.items.map((item, index) => (
                            <Button
                              key={index}
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                try {
                                  const response = await axios.get(
                                    `${API}/download/${purchase.transaction_ref}?product_id=${item.product_id}`,
                                    { headers: { Authorization: `Bearer ${token}` } }
                                  );
                                  if (response.data.download_url) {
                                    window.open(response.data.download_url, "_blank");
                                  } else if (response.data.mock) {
                                    toast.info("Mock download - PDF files not configured");
                                  }
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Download failed");
                                }
                              }}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download {item.title.substring(0, 20)}...
                            </Button>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <img src="/logo.png" alt="Great Messiah Publication" className="w-16 h-16 mx-auto mb-4 object-contain opacity-40 grayscale" />
                  <h3 className="font-heading text-xl font-bold mb-2">No purchases yet</h3>
                  <p className="text-muted-foreground mb-6">
                    You haven't purchased any mock examinations yet.
                  </p>
                  <Button asChild className="bg-primary">
                    <Link to="/mock-exams">Browse Mock Exams</Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Order History Tab */}
          <TabsContent value="order-history" data-testid="order-history-content">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Recent Orders
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Showing latest {Math.min(purchases.length, 5)} of {purchases.length} order{purchases.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {purchases.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      data-testid="view-all-orders-btn"
                    >
                      <Link to="/orders">View All Orders</Link>
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : purchases.length > 0 ? (
                  <div className="space-y-3">
                    {purchases.slice(0, 5).map((purchase) => (
                      <div
                        key={purchase.id || purchase.transaction_ref}
                        className="flex items-center justify-between p-4 rounded-xl border bg-card hover:bg-secondary/30 transition-colors"
                        data-testid={`order-row-${purchase.transaction_ref}`}
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <Package className="w-5 h-5 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-sm truncate">
                                {purchase.items?.map(i => i.title).join(", ") || "Order"}
                              </p>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Completed
                              </span>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {formatDate(purchase.created_at)}
                              </span>
                              <span>Ref: {purchase.transaction_ref}</span>
                              <span>{purchase.items?.length || 0} item{(purchase.items?.length || 0) !== 1 ? "s" : ""}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 ml-4">
                          <p className="font-bold text-primary whitespace-nowrap">{formatPrice(purchase.total_amount)}</p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => hideOrder(purchase.transaction_ref)}
                            className="text-muted-foreground hover:text-red-600 h-8 w-8 p-0"
                            title="Remove from history"
                            data-testid={`hide-order-${purchase.transaction_ref}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-secondary/50 flex items-center justify-center mx-auto mb-4">
                      <History className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="font-heading text-lg font-bold mb-2">No order history</h3>
                    <p className="text-muted-foreground text-sm mb-6">
                      You haven't made any purchases yet, or your history has been cleared.
                    </p>
                    <Button asChild className="bg-primary">
                      <Link to="/mock-exams">Browse Mock Exams</Link>
                    </Button>
                  </div>
                )}

                {purchases.length > 5 && (
                  <div className="mt-4 text-center">
                    <Button variant="outline" size="sm" asChild className="rounded-lg" data-testid="view-all-orders-bottom">
                      <Link to="/orders">View All {purchases.length} Orders</Link>
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Account Tab */}
          <TabsContent value="account">
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6 max-w-md">
                  <div className="flex items-center gap-4 p-4 bg-secondary/50 rounded-lg">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email Address</p>
                      <p className="font-medium">{user.email}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-secondary/50 rounded-lg">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Account Type</p>
                      <p className="font-medium capitalize">{user.user_type}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle>Profile Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileUpdate} className="space-y-4 max-w-md">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={profileData.name}
                      onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter your name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={profileData.phone}
                      onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="0501234567"
                    />
                  </div>
                  <Button type="submit" className="bg-primary">
                    Save Changes
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
